//
// # Layout Content
//

// Local Imports
import { schema } from "GenAlphaSchema";
import { model, layout, Bbox, Direction, Rotation } from "GenAlphaModel";
import { AsyncResult, Result, Ok, exhaust, logger, Context } from "CoreUtils";
import { type RedrawRequest } from "DrawCore/src/redraw";
import type { InteractionHost } from "../../interaction_host";
import * as changes from "DrawCore/src/changes";
import { type MousePos } from "DrawCore/src/mousepos";
import { type ModeHandler } from "../../modes";
import { EntityKind } from "DrawCore/src/entity";
import { ContentKind } from "DrawCore/src/content/kind";
import { InteractionBase } from "../interaction_base";
import type { FieldEdits } from "../fieldEdits";
import {
  LayoutRender,
  type DrcErrorInfo,
  type LayoutMarker,
} from "DrawCore/src/content/layout/render";
import { LayoutIdle } from "./modes";
import {
  InstanceEntity,
  ScalarInstanceEntity,
  Segment,
} from "DrawCore/src/content/layout/instance";
import {
  LayoutEntity,
  BlockageEntity,
} from "DrawCore/src/content/layout/layout";
import { PinEntity } from "DrawCore/src/content/layout/pin";
import { RouteEntity, SegmentEntity } from "DrawCore/src/content/layout/route";
import { ChangeRequests } from "./changes";
import { LayoutDisplayOptions, LayoutSelectability } from "DrawCore/src/draw";

export type Child = InstanceEntity | RouteEntity | PinEntity | BlockageEntity;
export type ChildKind = Child["entityKind"];
export type Selectable =
  | ScalarInstanceEntity
  | Segment
  | SegmentEntity
  | PinEntity;

//
// # Layout Content — Interaction Tier
//
export class LayoutContent extends InteractionBase<
  Child,
  Selectable,
  LayoutRender
> {
  override readonly contentKind = ContentKind.Layout;

  readonly changeRequests = new ChangeRequests(this); // Change-request handler

  /**
   * Per-category selectability. When a flag is false, items of that category
   * cannot be clicked, box-selected, or picked up by select-all.
   * Items still render normally - locking is purely behavioral.
   */
  selectability = LayoutSelectability.default();

  // --- Delegating getters to the render tier ---
  get data(): layout.Layout {
    return this.render.data;
  }
  get selectedSegments(): Map<InstanceEntity, Set<number>> {
    return this.render.selectedSegments;
  }
  get layoutEntity(): LayoutEntity {
    return this.render.layoutEntity;
  }
  get stack(): layout.Stack | null {
    return this.render.stack;
  }
  get pdk(): schema.pdk.PDK | null {
    return this.render.pdk;
  }
  get layouts(): model.IdentMap<layout.Layout> {
    return this.render.layouts;
  }
  get hiddenLayers(): Set<number> {
    return this.render.hiddenLayers;
  }
  get layoutDisplay(): LayoutDisplayOptions {
    return this.render.layoutDisplay;
  }
  get pdkId(): string | undefined {
    return this.render.pdkId;
  }
  get schematicImpl(): model.ident.Ident | undefined {
    return this.render.schematicImpl;
  }

  /**
   * Toggle visibility of a layer by its number.
   * Hides visible layers and shows hidden layers.
   * Automatically triggers a redraw after toggling.
   * @param layerNumber - The PDK layer number to toggle
   */
  toggleLayerVisibility = (layerNumber: number): void => {
    if (this.hiddenLayers.has(layerNumber)) {
      this.hiddenLayers.delete(layerNumber);
    } else {
      this.hiddenLayers.add(layerNumber);
    }
    // Invalidate the sprite cache so instances are re-rendered with new visibility
    this.editor.canvas.invalidate?.();
    this.editor.requestRedraw();
  };

  /**
   * Check if a layer is currently visible.
   * @param layerNumber - The PDK layer number to check
   * @returns true if the layer is visible, false if hidden
   */
  isLayerVisible = (layerNumber: number): boolean => {
    return !this.hiddenLayers.has(layerNumber);
  };

  /**
   * Show only the specified layer, hiding all others.
   * Used for Ctrl+Click "solo" mode in the layer info panel.
   * @param layerNumber - The PDK layer number to keep visible
   * @param allLayerNumbers - All layer numbers currently displayed in the panel
   */
  /**
   * Show all layers by clearing the hidden set.
   * Used by the 'A' key shortcut when the layer info panel is open.
   */
  showAllLayers = (): void => {
    this.hiddenLayers.clear();
    this.editor.canvas.invalidate?.();
    this.editor.requestRedraw();
  };

  /**
   * Hide all layers in the given set by adding them to the hidden set.
   * Equivalent to de-selecting every layer in the layer info panel.
   * @param allLayerNumbers - All layer numbers currently displayed in the panel
   */
  hideAllLayers = (allLayerNumbers: Iterable<number>): void => {
    for (const num of allLayerNumbers) {
      this.hiddenLayers.add(num);
    }
    this.editor.canvas.invalidate?.();
    this.editor.requestRedraw();
  };

  showOnlyLayer = (
    layerNumber: number,
    allLayerNumbers: Iterable<number>
  ): void => {
    this.hiddenLayers.clear();
    for (const num of allLayerNumbers) {
      if (num !== layerNumber) {
        this.hiddenLayers.add(num);
      }
    }
    this.editor.canvas.invalidate?.();
    this.editor.requestRedraw();
  };

  /**
   * Toggle a layout display option by key.
   * Mutates in place; canvas holds a shared reference so changes are picked up automatically.
   */
  toggleLayoutDisplay = (key: keyof LayoutDisplayOptions): void => {
    this.layoutDisplay[key] = !this.layoutDisplay[key];
    // Blockage toggle needs to update the status bar indicator
    if (key === "blockages") {
      this.editor.updateBlockagesIndicator();
    }
    this.editor.canvas.invalidate?.();
    this.editor.requestRedraw();
  };

  /**
   * Toggle a per-category selectability flag.
   * Items of categories with `false` cannot be selected (hit-tests skip them).
   */
  toggleSelectability = (key: keyof LayoutSelectability): void => {
    this.selectability[key] = !this.selectability[key];
    this.editor.requestRedraw();
  };

  // The interaction facade drives a render tier it owns. All entity/state
  // construction lives on `LayoutRender`; this ctor just wires the two together.
  private constructor(editor: InteractionHost, render: LayoutRender) {
    super(editor, render);
  }

  static new(editor: InteractionHost, data: layout.Layout): LayoutContent {
    return new LayoutContent(editor, LayoutRender.new(editor, data));
  }

  // Auto-initialize outlines from bbox when outline mode is toggled on.
  // Sets the current cell's outline (persisted via set-outline change)
  // and all sub-cell layouts' outlines (in-memory only, for display).
  initOutline = (): void => {
    // Init current cell's outline and persist
    if (!this.data.abs.outline) {
      const outline = model.Bbox.clone(this.data.abs.bbox);
      this.data.abs.outline = outline;
      const change = model.LayoutChange.setOutline(outline);
      this.editor.platform.notifyUIChange(change);
    }
    // Init sub-cell outlines in memory for display
    for (const lay of this.layouts.values()) {
      if (!lay.abs.outline) {
        lay.abs.outline = model.Bbox.clone(lay.abs.bbox);
      }
    }
  };

  // Set the current cell's outline to its computed content bbox (the union of
  // all placed instances, elements, and pins), persisted via a set-outline
  // change. Unlike `initOutline`, this always overwrites any existing outline
  // and uses the freshly recomputed scene bbox rather than the stored
  // `abs.bbox` — which is empty for instance-only container cells (the case
  // this exists for). Same persist mechanism as `initOutline`.
  setOutlineToBbox = (): void => {
    this.recalcBbox();
    const bbox = this.bbox;
    // Nothing placed yet: a degenerate (point) bbox makes no usable outline.
    if (bbox.min.x === bbox.max.x && bbox.min.y === bbox.max.y) {
      return;
    }
    const outline = model.Bbox.clone(bbox);
    this.data.abs.outline = outline;
    const change = model.LayoutChange.setOutline(outline);
    this.editor.platform.notifyUIChange(change);
  };

  static async fromSchema(
    editor: InteractionHost,
    data: schema.Layout
  ): Promise<Result<LayoutContent>> {
    // PDK loading now handled in Layout.fromSchema()
    // Use background context for client-side code (no request context available)
    const loadStart = performance.now();
    const rootIdent = model.ident.Ident.displayName(data.ident);
    logger.log(`[LayoutContent] Loading layout hierarchy: ${rootIdent}`);

    const make = await layout.Layout.fromSchema(
      Context.background(),
      data,
      editor.depot,
      "" // Browser uses root namespace
    );

    const loadMs = Math.round(performance.now() - loadStart);

    return make.andThenAsync(async (lay) => {
      const content = LayoutContent.fromModel(editor, lay);

      // Copy the backward reference to the schematic this layout implements
      if (data.schematicImpl) {
        content.render.schematicImpl = data.schematicImpl;
      }

      // Copy PDK identifier from the model
      if (lay.pdkId) {
        content.render.pdkId = lay.pdkId;
      }

      // Log hierarchy load timing
      const layoutCount = layout.Layout.layouts.size;
      logger.log(
        `[LayoutContent] Loaded ${layoutCount} layouts in ${loadMs}ms (root: ${rootIdent})`
      );

      // Log PDK status if loaded
      if (lay.pdk) {
        logger.log(
          `[LayoutContent] Using PDK ${lay.pdkId} with ${lay.pdk.layers.length} layers`
        );
      }

      // Backfill outline from bbox if unset. Idempotent: once outline is
      // persisted, subsequent loads see it set and this is a no-op.
      content.initOutline();

      return Result.Ok(content);
    });
  }

  static fromModel(
    editor: InteractionHost,
    data: layout.Layout
  ): LayoutContent {
    return new LayoutContent(editor, LayoutRender.new(editor, data));
  }

  // Convert into the serializable data model
  toData = (): layout.Layout => {
    return this.layoutEntity.toData();
  };

  // Resolve a Selectable to its owning Child.
  // For instances: ScalarInstanceEntity or Segment → InstanceEntity.
  // For routes: SegmentEntity → RouteEntity.
  resolveChild = (selectable: Selectable): Child | null => {
    // PinEntity is already a Child - resolve to itself
    if (selectable instanceof PinEntity) return selectable;

    const { implEntity } = this.layoutEntity;
    if (!implEntity) return null;

    // Check if it's a route SegmentEntity - resolve to parent RouteEntity
    if (selectable instanceof SegmentEntity) {
      for (const route of implEntity.routes.values()) {
        if (route.segments.has(selectable)) {
          return route;
        }
      }
      return null;
    }

    // Otherwise resolve instance selectables
    for (const instance of implEntity.instances.values()) {
      if (
        instance.inner.kind === "scalar" &&
        instance.inner.drawer === selectable
      ) {
        return instance;
      }
      if (
        instance.inner.kind === "segments" ||
        instance.inner.kind === "array"
      ) {
        if (
          selectable instanceof Segment &&
          selectable.parent === instance.inner.drawer
        ) {
          return instance;
        }
      }
    }
    return null;
  };

  // -- Per-segment selection helpers --

  // Select a single segment of a segmented instance.
  // Makes the parent instance the sole selection and records the segment index.
  selectSegment = (
    instance: InstanceEntity,
    segIndex: number
  ): RedrawRequest => {
    this.deselect(); // clears selection + selectedSegments
    this.selection.add(instance);
    this.lastSelected_ = instance;
    instance.highlight(true);
    this.selectedSegments.set(instance, new Set([segIndex]));
    this.notifySelection();
    return this.requestRedraw();
  };

  // Toggle a segment in/out of the per-segment selection (shift-click).
  // If the instance isn't selected yet, adds it.
  addSegmentToSelection = (
    instance: InstanceEntity,
    segIndex: number
  ): RedrawRequest => {
    if (!this.selection.has(instance)) {
      // First time selecting this instance
      this.selection.add(instance);
      instance.highlight(true);
      this.selectedSegments.set(instance, new Set([segIndex]));
    } else {
      // Instance already selected - toggle this segment
      const indices = this.selectedSegments.get(instance);
      if (indices) {
        if (indices.has(segIndex)) {
          indices.delete(segIndex);
          // If no segments left, deselect the whole instance
          if (indices.size === 0) {
            this.selectedSegments.delete(instance);
            this.selection.delete(instance);
            instance.highlight(false);
          }
        } else {
          indices.add(segIndex);
        }
      } else {
        // Whole-instance selection - switch to per-segment by recording this index.
        // All other segments become siblings (purple). We need to know total segment count,
        // but since we only track selected indices, just add the new one.
        this.selectedSegments.set(instance, new Set([segIndex]));
      }
    }
    // Update lastSelected_
    if (this.selection.size > 0) {
      this.lastSelected_ = instance;
    } else {
      this.lastSelected_ = null;
    }
    this.notifySelection();
    return this.requestRedraw();
  };

  // Select all segments of a segmented instance (no per-segment tracking).
  // This gives standard whole-instance highlight (all blue).
  selectAllSegmentsOf = (instance: InstanceEntity): RedrawRequest => {
    // Remove per-segment tracking - causes all segments to highlight as selected
    this.selectedSegments.delete(instance);
    if (!this.selection.has(instance)) {
      this.selection.add(instance);
      instance.highlight(true);
    }
    this.lastSelected_ = instance;
    this.notifySelection();
    return this.requestRedraw();
  };

  // Check whether a specific segment is in the selected set.
  isSegmentSelected = (instance: InstanceEntity, segIndex: number): boolean => {
    if (!this.selection.has(instance)) return false;
    const indices = this.selectedSegments.get(instance);
    // No per-segment tracking means whole-instance selected (all segments)
    if (!indices) return true;
    return indices.has(segIndex);
  };

  // Get the set of selected segment indices, or null if whole-instance is selected.
  getSelectedSegmentIndices = (
    instance: InstanceEntity
  ): Set<number> | null => {
    return this.selectedSegments.get(instance) ?? null;
  };

  // Override addToSelection to also clear per-segment tracking when
  // toggling an instance out of the selection.
  // Note: ContentBase.addToSelection is an arrow function field, so we
  // reimplement rather than calling super.addToSelection.
  override addToSelection = (entity: Child): RedrawRequest => {
    if (this.selection.has(entity)) {
      // Toggling off: clean up per-segment tracking for this instance.
      this.selectedSegments.delete(entity as InstanceEntity);
      this.selection.delete(entity);
      entity.highlight(false);
      if (this.selection.size > 0) {
        let last: Child | null = null;
        for (const e of this.selection) last = e;
        this.lastSelected_ = last;
      } else {
        this.lastSelected_ = null;
      }
    } else {
      this.selection.add(entity);
      this.lastSelected_ = entity;
      entity.highlight(true);
    }
    this.notifySelection();
    return this.requestRedraw();
  };

  // Override deselect to also clear per-segment tracking.
  override deselect = (): RedrawRequest | null => {
    this.selectedSegments.clear();
    for (const entity of this.selection) {
      entity.highlight(false);
    }
    this.selection.clear();
    this.lastSelected_ = null;
    this.notifySelection();
    return this.requestRedraw();
  };

  // Override select to also clear per-segment tracking.
  // Note: ContentBase.select is an arrow function field, so we reimplement
  // rather than calling super.select.
  override select = (entity: Child): RedrawRequest => {
    // Clear without notifying via deselect's notify; we notify once below for
    // the final single-entity state.
    this.selectedSegments.clear();
    for (const e of this.selection) e.highlight(false);
    this.selection.clear();
    this.selection.add(entity);
    this.lastSelected_ = entity;
    const redraw = entity.highlight(true);
    this.notifySelection();
    return redraw;
  };

  // Top-level instances and abstract pins are the layout entities the
  // Properties panel tracks; expose them so a refresh can re-locate the
  // selection by id. Segment-level and route selections lack a stable top-level
  // id and aren't restored (a refresh dropped them before this too).
  protected override selectableEntities = (): Iterable<Child> => {
    const entities: Array<Child> = [];
    const { implEntity, absEntity } = this.layoutEntity;
    if (implEntity) {
      for (const instance of implEntity.instances.values()) {
        entities.push(instance);
      }
    }
    for (const pin of absEntity.pins.values()) {
      entities.push(pin);
    }
    return entities;
  };

  // Select all entities whose bounding boxes intersect the given bbox.
  // Iterates top-level instances and tests their transformed bboxes.
  // For segmented instances, selects only the individual segments that
  // intersect rather than the whole instance.
  override selectAllInBbox = (
    bbox: model.Bbox,
    additive: boolean
  ): RedrawRequest | null => {
    if (!additive) this.deselect();

    const { implEntity } = this.layoutEntity;
    if (!implEntity) return this.requestRedraw();

    const hits: Array<Child> = [];

    if (this.layoutDisplay.instances && this.selectability.instances) {
      for (const instance of implEntity.instances.values()) {
        if (instance.inner.kind === "scalar") {
          // Scalar instance: test single bbox
          const { layout: lay } = instance.inner.drawer.data.target;
          const instBbox = model.Xform.transformBbox(
            instance.inner.drawer.xform,
            lay.abs.bbox
          );
          if (model.Bbox.intersects(bbox, instBbox)) {
            hits.push(instance);
          }
        } else if (
          instance.inner.kind === "segments" ||
          instance.inner.kind === "array"
        ) {
          // Segmented instance: test each segment individually.
          // No `seg.scalarEntity` null guard: the field is declared
          // non-nullable in `Segment` (instance.ts) and the constructor
          // unconditionally initializes it - strict-type-checked lint
          // would reject the dead check on changed lines.
          for (const seg of instance.inner.drawer.segments) {
            const { layout: lay } = seg.scalarEntity.data.target;
            const segBbox = model.Xform.transformBbox(
              seg.scalarEntity.xform,
              lay.abs.bbox
            );
            if (model.Bbox.intersects(bbox, segBbox)) {
              this.addSegmentToSelection(instance, seg.index);
            }
          }
        }
      }
    }

    // Include pins in box-select
    if (this.layoutDisplay.pins && this.selectability.pins) {
      for (const pin of this.layoutEntity.absEntity.pins.values()) {
        if (model.Bbox.intersects(bbox, pin.bbox())) {
          hits.push(pin);
        }
      }
    }

    // Routes are excluded from box-select in idle mode.
    // Route selection only happens in DrawRoute mode (Shift+R).

    // Select non-segment hits (scalar instances, pins) via the standard path
    if (hits.length > 0) {
      this.selectMultiple(hits, true); // always additive since we cleared above
    }

    return this.requestRedraw();
  };

  // Select all routes whose segments intersect the given bbox.
  // Used by RouteBoxSelect in DrawRoute mode.
  selectAllRoutesInBbox = (
    bbox: model.Bbox,
    additive: boolean
  ): RedrawRequest | null => {
    const hits: Array<Child> = [];
    const { implEntity } = this.layoutEntity;
    if (!implEntity) return this.requestRedraw();

    if (this.layoutDisplay.routes && this.selectability.routes) {
      for (const route of implEntity.routes.values()) {
        for (const seg of route.segments) {
          if (model.Bbox.intersects(bbox, seg.bbox)) {
            hits.push(route);
            break;
          }
        }
      }
    }
    return this.selectMultiple(hits, additive);
  };

  // Select every selectable entity in the current layout.
  // Behavior is mode-dependent (matches existing box-select gating):
  //   - DrawRoute mode: selects routes
  //   - any other mode (idle): selects instances + pins
  override selectAll = (): RedrawRequest | null => {
    if (this.editor.isRouteMode) {
      return this.selectAllRoutes();
    }
    return this.selectAllIdle();
  };

  // Select every selectable instance and pin in idle mode.
  // Routes are excluded - they are handled separately in DrawRoute mode.
  // Mirrors `selectAllInBbox` without the bbox filter.
  selectAllIdle = (): RedrawRequest | null => {
    this.deselect();

    const { implEntity } = this.layoutEntity;
    if (!implEntity) return this.requestRedraw();

    const hits: Array<Child> = [];

    if (this.layoutDisplay.instances && this.selectability.instances) {
      for (const instance of implEntity.instances.values()) {
        if (instance.inner.kind === "scalar") {
          hits.push(instance);
        } else if (
          instance.inner.kind === "segments" ||
          instance.inner.kind === "array"
        ) {
          // No `seg.scalarEntity` null guard needed - see note in
          // `selectAllInBbox` above. The field is non-nullable per its
          // declaration in `Segment` (instance.ts).
          for (const seg of instance.inner.drawer.segments) {
            this.addSegmentToSelection(instance, seg.index);
          }
        }
      }
    }

    if (this.layoutDisplay.pins && this.selectability.pins) {
      for (const pin of this.layoutEntity.absEntity.pins.values()) {
        hits.push(pin);
      }
    }

    // Always additive: `addSegmentToSelection` above already adds the parent
    // `InstanceEntity` to `this.selection` (on its first call per instance)
    // and populates `this.selectedSegments`. `selectMultiple(hits, false)`
    // would internally call `deselect()` and wipe those additions — so we
    // pass `true` to preserve them. We already cleared once at the top of
    // this method, so additive here is the correct choice (matches
    // `selectAllInBbox` above).
    if (hits.length > 0) {
      this.selectMultiple(hits, true);
    }
    return this.requestRedraw();
  };

  // Select every selectable route. Used by Select-All in DrawRoute mode.
  // Routes have no segment-style intermediate selection, so `additive` below
  // is functionally equivalent to either value — kept `true` to match the
  // `selectAllIdle` pattern above.
  selectAllRoutes = (): RedrawRequest | null => {
    this.deselect();

    const { implEntity } = this.layoutEntity;
    if (!implEntity) return this.requestRedraw();

    if (!this.layoutDisplay.routes || !this.selectability.routes) {
      return this.requestRedraw();
    }

    const hits: Array<Child> = [];
    for (const route of implEntity.routes.values()) {
      hits.push(route);
    }
    return this.selectMultiple(hits, true);
  };

  // Hit testing.
  // includeRoutes: when true, routes are checked before instances (used by DrawRoute mode).
  // Defaults to false so that idle mode ignores routes entirely.
  override whatdWeHit = (
    mousePos: MousePos,
    includeRoutes: boolean = false
  ): Selectable | null => {
    const { implEntity } = this.layoutEntity;
    if (!implEntity) return null;

    // Check routes FIRST - they are rendered on top of instances and should take priority.
    // Routes are thin (nanometer-scale widths) so expand the hit bbox by a few pixels
    // in world coordinates to match the visual min-pixel enforcement in rendering.
    if (
      includeRoutes &&
      this.layoutDisplay.routes &&
      this.selectability.routes
    ) {
      const HIT_TOLERANCE_PX = 8;
      const tolerance =
        HIT_TOLERANCE_PX / this.editor.canvas.worldTransforms.schToPage.scale;
      // Collect all segments whose expanded bbox contains the mouse, then
      // return the nearest one (by segment midpoint) so dense routing regions
      // always yield the most obvious hit rather than the first iterated one.
      let bestSeg: SegmentEntity | null = null;
      let bestDist = Infinity;
      const mouse = mousePos.sch;
      for (const route of implEntity.routes.values()) {
        for (const seg of route.segments) {
          if (!seg.hitTest(mousePos, tolerance)) continue;
          const mid = Bbox.center(seg.bbox);
          const dx = mid.x - mouse.x;
          const dy = mid.y - mouse.y;
          const dist = dx * dx + dy * dy;
          if (dist < bestDist) {
            bestDist = dist;
            bestSeg = seg;
          }
        }
      }
      if (bestSeg) return bestSeg;
    }

    // Check pins before instances - pins are small overlay elements
    // and should take priority when they overlap with instances.
    if (this.layoutDisplay.pins && this.selectability.pins) {
      for (const pin of this.layoutEntity.absEntity.pins.values()) {
        if (pin.hitTest(mousePos)) return pin;
      }
    }

    // Fall back to instances
    if (this.layoutDisplay.instances && this.selectability.instances) {
      for (const instance of implEntity.instances.values()) {
        if (
          instance.inner.kind === "scalar" &&
          instance.inner.drawer.hitTest(mousePos)
        ) {
          return instance.inner.drawer;
        }
        if (
          instance.inner.kind === "segments" ||
          instance.inner.kind === "array"
        ) {
          for (const seg of instance.inner.drawer.segments) {
            if (seg.hitTest(mousePos)) {
              return seg;
            }
          }
        }
      }
    }
    return null;
  };

  // Hit-test blockages at the given mouse position.
  // Returns the first blockage whose bbox contains the mouse, or null.
  // Only hits when blockages are visible (layoutDisplay.blockages is true).
  hitTestBlockage = (mousePos: MousePos): BlockageEntity | null => {
    if (!this.layoutDisplay.blockages) return null;
    if (!this.selectability.blockages) return null;
    const { implEntity } = this.layoutEntity;
    if (!implEntity) return null;
    const p = mousePos.sch;
    for (const blockage of this.layoutEntity.absEntity.blockages.values()) {
      if (Bbox.contains(blockage.data.bbox, p)) return blockage;
    }
    return null;
  };

  override idleMode = (): ModeHandler => new LayoutIdle(this);

  // Expand the abstract bounding box outline to encompass all current content.
  // Called after every change so the red outline grows when items are moved
  // (or added) beyond the existing extent.
  private expandAbstractBboxToFitContent = (): void => {
    const { bboxEntity } = this.layoutEntity.absEntity;

    // Recompute the scene bbox from all instances
    this.recalcBbox();
    const sceneBbox = this.bbox;
    // Skip if no content (empty bbox is origin-to-origin)
    if (
      sceneBbox.min.x === sceneBbox.max.x &&
      sceneBbox.min.y === sceneBbox.max.y
    ) {
      return;
    }

    const absBbox = bboxEntity.data.bbox;

    // Only expand - never shrink the outline
    const expanded = Bbox.encompassingBoth(absBbox, sceneBbox);

    // Skip the update if the outline already covers the scene
    if (
      expanded.min.x === absBbox.min.x &&
      expanded.min.y === absBbox.min.y &&
      expanded.max.x === absBbox.max.x &&
      expanded.max.y === absBbox.max.y
    ) {
      return;
    }

    bboxEntity.data = { ...bboxEntity.data, bbox: expanded };

    this.requestRedraw();
  };

  // Override base logChange to expand the abstract bbox after every mutation.
  override logChange(change: changes.Change<Child>): void {
    super.logChange(change);
    this.expandAbstractBboxToFitContent();
  }

  // Compute the bounding box of the currently selected entities.
  override selectionBbox = (): model.Bbox | null => {
    if (this.selection.size === 0) return null;
    const bboxes: Array<model.Bbox> = [];

    for (const entity of this.selection) {
      if (entity.entityKind === EntityKind.LayoutInstance) {
        (entity as InstanceEntity).collectBboxes(bboxes);
      } else if (entity.entityKind === EntityKind.LayoutPin) {
        bboxes.push((entity as PinEntity).bbox());
      }
    }

    return bboxes.length > 0 ? model.Bbox.getBboxUnion(bboxes) : null;
  };

  // Duplicate all selected layout instances, placing copies at an offset from the originals.
  // The offset is 50% of the selection bounding box size in each axis (minimum 100 units).
  override duplicateSelected = (): RedrawRequest | null => {
    if (this.selection.size === 0) return null;

    // Collect only InstanceEntity items from the selection (skip routes, pins)
    const instances: Array<InstanceEntity> = [];
    for (const entity of this.selection) {
      if (entity.entityKind === EntityKind.LayoutInstance) {
        instances.push(entity as InstanceEntity);
      }
    }
    if (instances.length === 0) return null;

    // Compute the offset for duplicate placement.
    // Use the maximum individual instance bbox dimensions (not the union of all instances).
    // Using the union bbox causes the offset to scale with inter-item distance,
    // producing unpredictable results when items are spread apart.
    let maxWidth = 0;
    let maxHeight = 0;
    const instBboxes: Array<model.Bbox> = [];
    for (const inst of instances) {
      instBboxes.length = 0;
      inst.collectBboxes(instBboxes);
      for (const b of instBboxes) {
        maxWidth = Math.max(maxWidth, b.max.x - b.min.x);
        maxHeight = Math.max(maxHeight, b.max.y - b.min.y);
      }
    }
    if (maxWidth === 0 && maxHeight === 0) return null;

    const dx = Math.max(maxWidth * 0.5, 100);
    const dy = Math.max(maxHeight * 0.5, 100);

    // Duplicate each instance using the LayoutEntity's createInstance factory
    const additions: Array<changes.Single<Child>> = [];
    const newInstances: Array<InstanceEntity> = [];
    // Count existing instances for name generation
    let nameCounter = 0;
    if (this.layoutEntity.implEntity) {
      this.layoutEntity.implEntity.instances.forEach(() => nameCounter++);
    }

    for (const inst of instances) {
      // Use toData() to get the current data from the inner drawer, not inst.data.
      // After a move, the inner drawer (ScalarInstanceEntity) has updated position
      // data, but InstanceEntity.data still holds the stale pre-move values because
      // Entity.update() creates a new data object, breaking the shared reference.
      const srcData = inst.toData();
      const newData: layout.Instance = {
        ...srcData,
        // Auto-increment name: strip trailing digits, append counter
        name: (srcData.name.replace(/\d+$/, "") || "I") + nameCounter,
        // Assign a new random ID so both client and server agree on the ID.
        // The server's IdMap.add() preserves existing IDs.
        id: model.randomId(),
        // Deep-clone and offset the placement
        place: srcData.place
          ? this.cloneAndOffsetPlace(srcData.place, dx, dy)
          : undefined,
      };
      nameCounter++;

      // Use createInstance which handles InstanceEntity construction and collection add
      const newInst = this.layoutEntity.createInstance(newData);
      additions.push(changes.Add.new(newInst));
      newInstances.push(newInst);
    }

    // Log all additions as a single atomic change (undoable in one Ctrl+Z)
    if (additions.length === 1) {
      this.logChange(additions[0]);
    } else {
      const combo = changes.Combo.fromArray(additions);
      if (combo.ok) this.logChange(combo.val);
    }

    // Select the newly created instances
    this.selectMultiple(newInstances);
    return this.requestRedraw();
  };

  // Clone a placement and apply a position offset.
  private cloneAndOffsetPlace = (
    place: NonNullable<layout.Instance["place"]>,
    dx: number,
    dy: number
  ): NonNullable<layout.Instance["place"]> => {
    switch (place.kind) {
      case "scalar": {
        const loc = place.xform.loc;
        return {
          ...place,
          xform: {
            ...place.xform,
            loc: { x: loc.x + dx, y: loc.y + dy },
          },
        };
      }
      case "segments": {
        return {
          ...place,
          segments: place.segments.map((seg) => {
            if (!seg) return seg;
            return {
              ...seg,
              loc: { x: seg.loc.x + dx, y: seg.loc.y + dy },
            };
          }),
        };
      }
      case "array": {
        // Clone the array xform with offset origin
        const arr = place.array;
        return {
          ...place,
          array: {
            ...arr,
            loc: { x: arr.loc.x + dx, y: arr.loc.y + dy },
          },
        };
      }
      default:
        return place;
    }
  };

  // Flip all selected layout instances in the given direction.
  flipSelected = (dir: Direction): RedrawRequest | null => {
    if (this.selection.size === 0) return null;

    const singleChanges: Array<changes.Single<Child>> = [];
    for (const entity of this.selection) {
      if (entity.entityKind !== EntityKind.LayoutInstance) continue;
      const inst = entity as InstanceEntity;
      const from = inst.toData();
      const flipped = this.flipInstanceData(from, dir);
      if (!flipped) continue;
      inst.update(flipped);
      singleChanges.push(
        changes.update({ target: inst, from, to: inst.toData() })
      );
    }

    if (singleChanges.length === 1) {
      this.logChange(singleChanges[0]);
    } else if (singleChanges.length > 1) {
      const combo = changes.Combo.fromArray(singleChanges);
      if (combo.ok) this.logChange(combo.val);
    }
    return this.requestRedraw();
  };

  // Rotate all selected layout instances by 90 degrees.
  rotateSelected = (): RedrawRequest | null => {
    if (this.selection.size === 0) return null;

    const singleChanges: Array<changes.Single<Child>> = [];
    for (const entity of this.selection) {
      if (entity.entityKind !== EntityKind.LayoutInstance) continue;
      const inst = entity as InstanceEntity;
      const from = inst.toData();
      const rotated = this.rotateInstanceData(from);
      if (!rotated) continue;
      inst.update(rotated);
      singleChanges.push(
        changes.update({ target: inst, from, to: inst.toData() })
      );
    }

    if (singleChanges.length === 1) {
      this.logChange(singleChanges[0]);
    } else if (singleChanges.length > 1) {
      const combo = changes.Combo.fromArray(singleChanges);
      if (combo.ok) this.logChange(combo.val);
    }
    return this.requestRedraw();
  };

  // Flip a layout instance's placement data in the given direction.
  private flipInstanceData = (
    data: layout.Instance,
    dir: Direction
  ): Partial<layout.Instance> | null => {
    if (!data.place) return null;
    const key = dir === Direction.Horiz ? "horiz" : "vert";
    switch (data.place.kind) {
      case "scalar": {
        const xform = data.place.xform;
        return {
          place: {
            kind: "scalar",
            xform: {
              ...xform,
              reflect: { ...xform.reflect, [key]: !xform.reflect[key] },
            },
          },
        };
      }
      case "segments": {
        return {
          place: {
            kind: "segments",
            segments: data.place.segments.map((seg) => {
              if (!seg) return seg;
              return {
                ...seg,
                reflect: { ...seg.reflect, [key]: !seg.reflect[key] },
              };
            }),
          },
        };
      }
      case "array":
        logger.log("TODO: flip for array placement not yet supported");
        return null;
      default:
        return null;
    }
  };

  // Rotate a layout instance's placement data by 90 degrees.
  private rotateInstanceData = (
    data: layout.Instance
  ): Partial<layout.Instance> | null => {
    if (!data.place) return null;
    switch (data.place.kind) {
      case "scalar": {
        const xform = data.place.xform;
        return {
          place: {
            kind: "scalar",
            xform: {
              ...xform,
              rotation: Rotation.next(xform.rotation ?? 0),
            },
          },
        };
      }
      case "segments": {
        return {
          place: {
            kind: "segments",
            segments: data.place.segments.map((seg) => {
              if (!seg) return seg;
              return {
                ...seg,
                rotation: Rotation.next(seg.rotation ?? 0),
              };
            }),
          },
        };
      }
      case "array":
        logger.log("TODO: rotate for array placement not yet supported");
        return null;
      default:
        return null;
    }
  };

  // Add a child entity
  override addEntity = (entity: Child, logChange: boolean): unknown => {
    const { entityKind } = entity;
    switch (entityKind) {
      case EntityKind.LayoutInstance: {
        return this.layoutEntity.addInstance(entity);
      }
      case EntityKind.LayoutPin: {
        return this.layoutEntity.addPin(entity);
      }
      case EntityKind.LayoutRoute: {
        return this.layoutEntity.addRoute(entity);
      }
      case EntityKind.LayoutBlockage: {
        return this.layoutEntity.addBlockage(entity);
      }
      default:
        exhaust(entityKind);
    }
  };

  // Remove a child
  // FIXME: follow the `logChange` secondary argument's instruction!
  override removeEntity = (child: Child, logChange: boolean): boolean => {
    const { id } = child.data;
    if (id === undefined) return false;
    const { layoutEntity } = this;
    const { implEntity } = layoutEntity;

    switch (child.entityKind) {
      case EntityKind.LayoutPin:
        return layoutEntity.absEntity.pins.remove(id);
      case EntityKind.LayoutInstance: {
        if (!implEntity) return false;
        return implEntity.instances.remove(id);
      }
      case EntityKind.LayoutRoute: {
        if (!implEntity) return false;
        return implEntity.routes.remove(id);
      }
      case EntityKind.LayoutBlockage:
        return layoutEntity.absEntity.blockages.remove(id);
      default:
        exhaust(child);
    }
  };

  // Fetch a layout. If the layout is not found in local memory it is requested from the platform.
  getLayout = async (ident: model.Ident): AsyncResult<layout.Layout> => {
    // Check the local cache first
    const existing = model.layout.Layout.layouts.get(ident);
    if (existing) {
      return Result.Ok(existing); // Already here, return it
    }
    const r = await this.editor.requestLayout(ident);
    // Use background context for client-side code (no request context available)
    return r.andThenAsync((s) =>
      model.layout.Layout.fromSchema(
        Context.background(),
        s,
        this.editor.depot,
        ""
      )
    );
  };

  override applyChangeRequest = async (
    changeList: Array<unknown>
  ): Promise<Result<unknown>> => {
    // Convert the message-stuff to our in-memory `Combo` change-list class
    const conv = await this.changeRequests.convertChangeRequest(changeList);
    // And apply everything in there. Do NOT call logChange here: this method
    // handles changes broadcast from the server (e.g. remote collaborator
    // edits, or LLM/Copilot tool changes). logChange would (a) push to the
    // originator's undo stack — wrong for remote edits, and (b) call
    // sendChangeMessage, which would re-broadcast the change back to the
    // server in a feedback loop. Matches schematic's applyChangeRequest.
    return conv.andThen((combo) => Ok(this.applyChange(combo)));
  };

  // Apply a list of server-originated changes locally AND push them to the
  // undo stack as a single Combo, without re-sending to the server (it's
  // already there). See ContentBase.applyServerOriginatedChange for the full
  // rationale.
  override applyServerOriginatedChange = async (
    rawChanges: Array<unknown>
  ): AsyncResult<unknown> => {
    const conv = await this.changeRequests.convertChangeRequest(rawChanges);
    if (conv.err) return conv;
    const combo = conv.val;
    this.applyChange(combo);
    // logChange would also call sendChangeMessage, which would round-trip the
    // change back to the server. Use recordChange to push to changeLog and
    // emit the [undo-log] line without re-sending.
    this.recordChange(combo);
    return Result.Ok(null);
  };

  // Apply a `Change`, generally as part of an undo or redo operation.
  override applyChange = (change: changes.Change<Child>): unknown => {
    const inner = () => {
      switch (change.kind) {
        case changes.Kind.Add: {
          return this.addEntity(change.entity, false);
        }
        case changes.Kind.Remove: {
          return this.removeEntity(change.entity, false);
        }
        case changes.Kind.Update: {
          return change.apply(); // Reminder when undo'ing, this is the *inverse* change! Always `apply`.
        }
        case changes.Kind.Combo: {
          return change.changes.forEach(this.applyChange);
        }
        default:
          exhaust(change); // Exhaustiveness check
      }
    };
    inner();
    return null;
  };

  //
  // # LVS Error Highlighting
  //

  /** Entities currently highlighted for LVS error display */
  private _lvsHighlightedEntities: Array<{
    highlight: (state: boolean) => RedrawRequest;
    clearPinHighlights?: () => RedrawRequest;
  }> = [];

  /** Entities currently highlighted for DRC error display */
  private _drcHighlightedEntities: Array<{
    highlight: (state: boolean) => RedrawRequest;
  }> = [];

  /**
   * Highlight layout elements related to an LVS error.
   * Clears any previous highlights, then highlights the relevant entities.
   * Pass null to clear all highlights without adding new ones.
   */
  highlightLvsError = (error: LvsErrorInfo | null): RedrawRequest => {
    // Clear previous LVS highlights
    for (const entity of this._lvsHighlightedEntities) {
      entity.highlight(false);
      entity.clearPinHighlights?.();
    }
    this._lvsHighlightedEntities = [];

    if (!error) {
      return this.requestRedraw();
    }

    // Highlight based on error type
    switch (error.kind) {
      case "open":
      case "short": {
        // Highlight all routes on this net
        if (error.net) {
          const routes = this.layoutEntity.findRoutesByNet(error.net);
          for (const route of routes) {
            route.highlight(true);
            this._lvsHighlightedEntities.push(route);
          }
        }
        // Also highlight the relevant pins if specified
        if (error.pins && error.pins.length > 0) {
          this.highlightLvsPins(error.pins);
        }
        break;
      }
      case "missing_instance":
      case "extra_instance": {
        // Highlight the instance
        // TODO: Provide user feedback when instance not found (e.g., console warning or UI indicator)
        if (error.instance) {
          const result = this.findInstanceWithArrayFallback(error.instance);
          if (result) {
            result.inst.highlight(true);
            this._lvsHighlightedEntities.push(result.inst);
          }
        }
        break;
      }
      case "missing_pin":
      case "extra_pin": {
        // Highlight the pins on instances or layout-level pins
        if (error.pins && error.pins.length > 0) {
          this.highlightLvsPins(error.pins);
        }
        break;
      }
    }

    return this.requestRedraw();
  };

  /**
   * Find an instance by name, falling back to the base name for arrayed instances.
   * e.g., "MP2.0" -> tries "MP2.0" first, then "MP2"
   * Returns the instance and optionally the segment index if it was an arrayed reference.
   */
  private findInstanceWithArrayFallback = (
    instanceName: string
  ): { inst: InstanceEntity; segmentIndex?: number } | undefined => {
    // Try exact name first
    const inst = this.layoutEntity.findInstanceByName(instanceName);
    if (inst) return { inst };

    // Try stripping arrayed instance suffix (e.g., "MP2.0" -> "MP2")
    const match = instanceName.match(/^(.+)\.(\d+)$/);
    if (match) {
      const baseInst = this.layoutEntity.findInstanceByName(match[1]);
      if (baseInst) {
        return { inst: baseInst, segmentIndex: parseInt(match[2], 10) };
      }
    }
    return undefined;
  };

  /**
   * Highlight multiple pins from an LVS error.
   * Each pin string is in "instance.pin" format (e.g., "M1.d") or "PIN:name" for layout-level pins.
   * Groups by instance to avoid duplicate instance highlights.
   */
  private highlightLvsPins = (pins: string[]): void => {
    // Group pins by instance to avoid duplicate lookups and highlights
    const instancePins = new Map<string, string[]>();
    const layoutPins: string[] = [];

    for (const pinField of pins) {
      const parsed = parseLvsPinField(pinField);
      if (parsed.instance) {
        const existing = instancePins.get(parsed.instance) ?? [];
        existing.push(parsed.pin);
        instancePins.set(parsed.instance, existing);
      } else {
        layoutPins.push(parsed.pin);
      }
    }

    // Highlight instance pins
    for (const [instanceName, pinNames] of instancePins) {
      const result = this.findInstanceWithArrayFallback(instanceName);
      if (result) {
        const { inst, segmentIndex } = result;
        inst.highlight(true);
        this._lvsHighlightedEntities.push(inst);

        // For arrayed instances with a segment index, highlight the specific segment's pin
        if (
          segmentIndex !== undefined &&
          (inst.inner.kind === "segments" || inst.inner.kind === "array")
        ) {
          const segment = inst.inner.drawer.segments[segmentIndex];
          if (segment) {
            for (const pinName of pinNames) {
              segment.scalarEntity.highlightPin(pinName);
            }
          }
        } else {
          // Scalar instance - highlight pins directly
          for (const pinName of pinNames) {
            inst.highlightPin(pinName);
          }
        }
      }
    }

    // Highlight layout-level pins
    for (const pinName of layoutPins) {
      const pin = this.layoutEntity.findPinByName(pinName);
      if (pin) {
        pin.highlight(true);
        this._lvsHighlightedEntities.push(pin);
      }
    }
  };

  //
  // ## DRC Error Highlighting
  //

  /**
   * Highlight layout elements related to a DRC error.
   * Clears any previous highlights, then highlights the relevant instances.
   * Pass null to clear all highlights without adding new ones.
   */
  highlightDrcError = (error: DrcErrorInfo | null): RedrawRequest => {
    // Clear previous DRC highlights
    for (const entity of this._drcHighlightedEntities) {
      entity.highlight(false);
    }
    this._drcHighlightedEntities = [];
    this.render.drcHighlightBbox = null;

    if (!error) {
      return this.requestRedraw();
    }

    // Highlight instances involved in the error
    if (error.instances && error.instances.length > 0) {
      for (const instanceName of error.instances) {
        const result = this.findInstanceWithArrayFallback(instanceName);
        if (result) {
          result.inst.highlight(true);
          this._drcHighlightedEntities.push(result.inst);
        }
      }
    }

    // Highlight the error region for errors that carry a bbox (e.g. route-width
    // violations, which reference a route segment rather than an instance).
    this.render.drcHighlightBbox = error.bbox ?? null;

    return this.requestRedraw();
  };

  /** Draw these labelled boxes over the cell, replacing any drawn before. */
  setMarkers = (markers: ReadonlyArray<LayoutMarker>): RedrawRequest => {
    this.render.markers = markers;
    return this.requestRedraw();
  };

  //
  // ## Parasitics Net Highlighting
  //

  /** Entities currently highlighted for parasitics net display */
  private _parasiticsHighlightedRoutes: Array<{
    highlight: (state: boolean) => RedrawRequest;
  }> = [];

  /**
   * Highlight routes belonging to one or two nets for parasitics visualization.
   * Pass null for both to clear all highlights.
   * Pass one net to highlight that net's routes.
   * Pass two nets to highlight a coupling pair.
   */
  highlightParasiticsNet = (
    netA: string | null,
    netB?: string
  ): RedrawRequest => {
    // Clear previous parasitics highlights
    for (const route of this._parasiticsHighlightedRoutes) {
      route.highlight(false);
    }
    this._parasiticsHighlightedRoutes = [];

    if (!netA) {
      return this.requestRedraw();
    }

    // Highlight routes for netA
    const routesA = this.layoutEntity.findRoutesByNet(netA);
    for (const route of routesA) {
      route.highlight(true);
      this._parasiticsHighlightedRoutes.push(route);
    }

    // If netB is specified (coupling pair), highlight its routes too
    if (netB) {
      const routesB = this.layoutEntity.findRoutesByNet(netB);
      for (const route of routesB) {
        route.highlight(true);
        this._parasiticsHighlightedRoutes.push(route);
      }
    }

    return this.requestRedraw();
  };

  // Apply buffered Properties-Panel field edits as a single atomic change.
  // Handles layout pins (name / layer / pinType / position, the last two
  // through the pin's shapes) and layout
  // instances (name / position / rotation / reflected). Generator-param edits
  // for layout instances are NOT handled here — they route through the
  // update-instance endpoint in the host (the documented server-regen seam).
  override applyFieldEdits = (
    edits: FieldEdits
  ): Result<void, { why: string }> => {
    if (edits.entityKind === EntityKind.LayoutPin.valueOf()) {
      return this.applyLayoutPinFieldEdits(edits);
    }
    if (edits.entityKind === EntityKind.LayoutInstance.valueOf()) {
      return this.applyLayoutInstanceFieldEdits(edits);
    }
    return Result.Err({ why: "unsupported-entity-kind" });
  };

  // Build + apply + log a single merged Update for a layout pin.
  private applyLayoutPinFieldEdits(
    edits: FieldEdits
  ): Result<void, { why: string }> {
    const pin = this.layoutEntity.findPin(edits.id);
    if (!pin) return Result.Err({ why: "layout-pin-not-found" });

    const oldData = pin.data;
    // Position moves every shape so the pin's anchor lands on it.
    let shapes = oldData.shapes;
    if (edits.position !== undefined) {
      shapes = pin.shapesAnchoredAt(edits.position);
    }
    // Layer is a one-shape pin's: it moves that shape to the layer. A pin with
    // several has no one layer to edit.
    if (edits.layer !== undefined) {
      const { stack } = this.data;
      if (!stack) return Result.Err({ why: "no-stack" });
      const metalLayer = model.layout.Stack.findMetalLayer(stack, edits.layer);
      if (!metalLayer.ok) return Result.Err({ why: "layer-not-found" });
      const sole = schema.pinSoleShape({ ...oldData, shapes });
      if (sole.err) return Result.Err({ why: sole.val.message });
      shapes = [{ ...sole.val, layer: metalLayer.val.layer }];
    }

    const newData: layout.LayoutPin = {
      ...oldData,
      ...(edits.name !== undefined ? { name: edits.name } : {}),
      ...(edits.pinType !== undefined ? { pinType: edits.pinType } : {}),
      shapes,
    };

    pin.update(newData);
    this.logChange(
      changes.update({ target: pin, from: oldData, to: { ...newData } })
    );
    return Result.Ok(undefined);
  }

  // Build + apply + log a single merged Update for a layout instance
  // (name / position / rotation / reflected). Only scalar placements carry a
  // single editable xform; for non-scalar placements only the name is edited.
  private applyLayoutInstanceFieldEdits(
    edits: FieldEdits
  ): Result<void, { why: string }> {
    const instance = this.layoutEntity.findInstance(edits.id);
    if (!instance) return Result.Err({ why: "layout-instance-not-found" });

    const oldData = instance.data;

    // Build PARTIAL from/to dicts (only changed fields). The server merges by
    // id; sending the full data would include the runtime-resolved `target`.
    const from: Partial<layout.Instance> = {};
    const to: Partial<layout.Instance> = {};

    if (edits.name !== undefined) {
      from.name = oldData.name;
      to.name = edits.name;
    }

    const place = oldData.place;
    if (
      place !== undefined &&
      place.kind === "scalar" &&
      (edits.position !== undefined ||
        edits.rotation !== undefined ||
        edits.reflected !== undefined)
    ) {
      const oldXform = place.xform;
      const newXform: layout.LayoutXform = {
        loc:
          edits.position !== undefined
            ? { x: edits.position.x, y: edits.position.y }
            : oldXform.loc,
        // Convert the panel's (rotation, reflected) back to layout reflect
        // flags. `vert` reflect maps to reflected=true with baseRotation 0,
        // which round-trips convertSomeOrientation for the panel's values.
        reflect:
          edits.reflected !== undefined
            ? { horiz: false, vert: edits.reflected }
            : oldXform.reflect,
        rotation: edits.rotation ?? oldXform.rotation,
      };
      from.place = { kind: "scalar", xform: oldXform };
      to.place = { kind: "scalar", xform: newXform };
    }

    instance.update(to);
    this.logChange(changes.update({ target: instance, from, to }));
    return Result.Ok(undefined);
  }

  // Set the explicit width (nm) of one segment of an existing route (the
  // segment leaving path point `segIdx`), or null to clear it to the layer
  // default. Returns false if the route or segment index is not found.
  // `width` is expected pre-normalized (grid-snapped, clamped) by the caller.
  updateSegmentWidth(
    routeId: number,
    segIdx: number,
    width: number | null
  ): boolean {
    const route = this.layoutEntity.findRoute(routeId);
    if (!route) return false;
    const oldPath = route.data.path;
    if (segIdx < 0 || segIdx >= oldPath.length) return false;
    const newPath = oldPath.map((rp, i) =>
      i === segIdx ? { ...rp, width } : rp
    );
    route.update({ path: newPath });
    this.logChange(
      changes.update({
        target: route,
        from: { path: oldPath },
        to: { path: newPath },
      })
    );
    this.editor.requestRedraw();
    return true;
  }
}

/**
 * LVS error information needed for highlighting.
 * This interface matches the relevant fields from the IDE's LvsError type.
 */
export interface LvsErrorInfo {
  kind: // Name-based matching errors
  | "open"
    | "short"
    | "missing_instance"
    | "extra_instance"
    | "missing_pin"
    | "extra_pin"
    // Topology-based matching errors
    | "unmatched_schematic_net"
    | "unmatched_layout_net"
    | "unmatched_schematic_device"
    | "unmatched_layout_device"
    | "structural_mismatch";
  net?: string;
  instance?: string;
  pins?: string[];
}

/**
 * Parse an LVS pin field into instance name and pin name.
 * Format: "instance.pin" (e.g., "M1.d") or "PIN:name" for layout-level pins.
 * Returns { instance, pin } where instance is undefined for layout-level pins.
 */
function parseLvsPinField(pinField: string): {
  instance?: string;
  pin: string;
} {
  if (pinField.startsWith("PIN:")) {
    return { pin: pinField.slice(4) };
  }
  if (pinField.includes(".")) {
    const parts = pinField.split(".");
    const pin = parts.pop()!;
    const instance = parts.join(".");
    // Guard against malformed input like "." or ".pin"
    if (instance && pin) {
      return { instance, pin };
    }
  }
  return { pin: pinField };
}
